import {
  api,
  AvailableWritingCertificationSet,
  CertificationBasicDataOfLong,
  PagedResultOfWritingCertificationListItem,
  WritingCertification,
  WritingCertificationCalculation,
  WritingCertificationCalculationDetails,
  WritingCertificationCalculationResult,
  WritingCertificationDetails,
  WritingCertificationRate,
} from 'api';
import { rtkQueryApi } from 'store/rtkQueryApi';

export const writingCertificationApi = rtkQueryApi.injectEndpoints({
  endpoints: (builder) => ({
    writingCertificationList: builder.query<
      PagedResultOfWritingCertificationListItem,
      { examinerId: number; page: number; count: number }
    >({
      query: (params) => api.writingCertification.getExaminerWritingCertifications(params),
      providesTags: ['WritingCertificationList'],
    }),

    writingCertificationDetails: builder.query<WritingCertificationDetails, { id: number }>({
      query: ({ id }) => api.writingCertification.get({ id }),
      providesTags: ['WritingCertification'],
    }),

    writingNextCertDate: builder.query<string, { examinerId: number }>({
      query: ({ examinerId }) => api.writingCertification.getNextCertificationsDate({ examinerId }),
      providesTags: ['WritingCertificationList'],
    }),

    writingCertRates: builder.query<WritingCertificationRate[], { certificationId: number }>({
      query: ({ certificationId }) =>
        api.writingCertificationRate.getWritingCertificationRates({ certificationId }),
      providesTags: ['WritingCertification'],
    }),

    availableWritingSets: builder.query<AvailableWritingCertificationSet[], { examinerId: number }>(
      {
        query: ({ examinerId }) =>
          api.writingCertificationSet.getExaminerWritingCertificationsSets({ examinerId }),
        providesTags: ['WritingCertification'],
      }
    ),

    writingIsAttemptsExceeded: builder.query<boolean, { examinerId: number; setId: number }>({
      query: ({ examinerId, setId }) =>
        api.writingCertification.isNumberOfAttemptsExceeded({ examinerId, setId }),
      providesTags: ['WritingCertification'],
    }),

    writingIsGapExceeded: builder.query<
      boolean,
      { examinerId: number; setId: number; certificationDate?: string | null }
    >({
      query: ({ examinerId, setId, certificationDate }) =>
        api.writingCertification.isGapBetweenAttemptsExceeded({
          examinerId,
          setId,
          certificationDate,
        }),
      providesTags: ['WritingCertification'],
    }),

    writingCertIsDeletable: builder.query<boolean, { certificationId: number }>({
      query: ({ certificationId }) => api.writingCertification.canBeDeleted({ certificationId }),
      providesTags: ['WritingCertification'],
    }),

    writingLastPassedCertDate: builder.query<CertificationBasicDataOfLong, { examinerId: number }>({
      query: ({ examinerId }) =>
        api.writingCertification.getLastPassedCertificationBasicData({ examinerId }),
      providesTags: ['WritingCertificationList'],
    }),

    calculateWritingCert: builder.mutation<
      WritingCertificationCalculationResult,
      WritingCertificationCalculation
    >({
      query: (data) => api.writingCertification.doCalculation(data),
    }),

    saveWritingCert: builder.mutation<WritingCertification, WritingCertificationCalculationDetails>(
      {
        query: (data) => api.writingCertification.saveCertification(data),
        invalidatesTags: [
          'WritingCertification',
          'WritingCertificationList',
          'ExaminerStatus',
          'Examiner',
        ],
      }
    ),

    deleteWritingCert: builder.mutation<void, { certificationId: number; examinerId: number }>({
      query: ({ certificationId, examinerId }) =>
        api.writingCertification.delete({ certificationId, examinerId }),
      invalidatesTags: ['WritingCertificationList', 'ExaminerStatus', 'Examiner'],
    }),
  }),
});

export const {
  useWritingCertificationListQuery,
  useWritingCertificationDetailsQuery,
  useWritingNextCertDateQuery,
  useWritingCertRatesQuery,
  useAvailableWritingSetsQuery,
  useWritingIsAttemptsExceededQuery,
  useWritingIsGapExceededQuery,
  useWritingCertIsDeletableQuery,
  useWritingLastPassedCertDateQuery,
  useCalculateWritingCertMutation,
  useSaveWritingCertMutation,
  useDeleteWritingCertMutation,
} = writingCertificationApi;
